import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ServicingBaseClass } from '@common/components';

@Injectable()
export class ReceiptSearchService extends ServicingBaseClass {
  constructor() {
    super();
  }
  override destroy(): void {}

  /**
   * ドキュメントにPDFビューをレンダリングします。
   *
   * @param {string} pdfBase64str
   * @param {Document} document
   * @return {void}
   */
  renderPdfViewToDocument(pdfBase64str: string, document: Document, pdfName: string): void {
    var pdfBinary = atob(pdfBase64str);
    var pdfBuffer = new Uint8Array(pdfBinary.length);
    for (let i = 0; i < pdfBinary.length; i++) {
      pdfBuffer[i] = pdfBinary.charCodeAt(i);
    }

    const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const embedDom = createEmbedNode(url);
    document.title = pdfName;
    document.body.append(embedDom);

    function createEmbedNode(url: string) {
      const node = document.createElement('embed');
      node.src = `${url}#toolbar=1`;
      node.style.width = '100vw';
      node.style.height = '100vh';
      return node;
    }
  }

  downloadPDF(base64str: string, pdfName: string): void {
    const byteCharacters = atob(base64str);
    const byteArray = Array.from(byteCharacters, (char) => char.charCodeAt(0));
    const blob = new Blob([new Uint8Array(byteArray)], { type: 'application/pdf' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = pdfName;
    link.click();
    link.remove();
  }

  /**
   * 全角と半角の文字数カウント
   * 全角の場合は2文字分としてカウントする
   *
   * @param control
   * @returns
   */
  countCharacters = (str: String): number => {
    let count = 0;
    for (let i = 0; i < str.length; i++) {
      const chatCode = str.charCodeAt(i);
      if (chatCode <= 255) {
        // 半角文字の場合
        count++;
      } else {
        // 全角文字の場合
        count += 2;
      }
    }
    return count;
  };

  /**
   * 提携追加：航空券番号の先頭から4～6桁目が252以外の場合エラーを返す
   * （NH0、NH3 PNRの判定）
   *
   * @param control
   * @returns
   */
  eTicketNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const eTicketNumber = control.get('eTicketNumber');
    if (eTicketNumber?.value.slice(3, 6) !== '252' && eTicketNumber?.value !== '') return { isETicketNumberNH: true };
    return null;
  };

  /**
   * 文字数が全角57桁、半角114桁字以内でない場合エラーを返す
   *
   * @param control
   * @returns
   */
  receiptAddressValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const receiptAddress = control.get('receiptAddress');
    // 入力された宛名が57字以下である場合
    if (receiptAddress?.value.length <= 57) return null;
    // 入力された宛名が144字を超えている場合
    if (receiptAddress?.value.length > 114) return { isReceiptAddressOverDigits: true };
    return this.countCharacters(receiptAddress?.value) > 114 ? { isReceiptAddressOverDigits: true } : null;
  };

  /**
   * 航空券番号検索：搭乗者名（名）、搭乗者名（姓）の合計桁数が55桁を超える場合はエラーを返す
   *
   * @param control
   * @returns
   */
  travelerNameDigitsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const firstName = control.get('firstName');
    const lastName = control.get('lastName');
    return firstName?.value?.length + lastName?.value?.length > 55 ? { isNameOverDigits: true } : null;
  };

  /**
   * 指定のバリデーションエラーを複数のフォームコントロールに設定する。
   *
   * @param formGroup 対象のフォームグループ
   * @param errorId バリデーションエラーのキー
   * @param controlNames エラーをセットするコントロール名のリスト
   */
  setValidationError(formGroup: FormGroup, errorId: string, controlNames: ReadonlyArray<string>) {
    controlNames.forEach((control) => {
      if (control) {
        const controlErrors: ValidationErrors | null | undefined = formGroup?.get(control)?.errors;
        const newErrors = controlErrors ? controlErrors : {};
        newErrors[errorId] = null;
        formGroup?.get(control)?.setErrors(newErrors);
      }
    });
  }

  /**
   * 指定のバリデーションエラーを複数のフォームコントロールから除去する。
   *
   * @param formGroup 対象のフォームグループ
   * @param errorId バリデーションエラーのキー
   * @param controlNames エラーを除去するコントロール名のリスト
   */
  removeValidationError(formGroup: FormGroup, errorId: string, controlNames: ReadonlyArray<string>) {
    controlNames.forEach((control) => {
      const controlErrors: ValidationErrors | null | undefined = formGroup?.get(control)?.errors;
      if (controlErrors) {
        var keys = Object.keys(controlErrors);
        if (keys.includes(errorId)) {
          const removeProp = errorId;
          const { [removeProp]: remove, ...rest } = controlErrors;
          // errorIdを除去した内容をセット。他にエラーがなければ null をセットすることでエラー状態をクリア。
          formGroup?.get(control)?.setErrors(keys.length === 1 ? null : rest);
        }
      }
    });
  }
}
